import { useEffect, useState, useSyncExternalStore } from "react";
import { useAuth } from "@/lib/auth";
import apiClient from "@/lib/api";
import {
  getGuestCareerStage,
  type CareerStageValue,
} from "@/lib/guestCareerStage";
import {
  subscribeProfileRevalidation,
  getProfileRevision,
  getServerProfileRevision,
} from "@/lib/profileRevalidation";

// 練習一覧・面接手動選択・スクリプト作成で、質問を新卒/中途区分で絞り込むための区分解決フック。
// - ログイン済: プロフィール区分（GET /users/me の profile.career_stage）
// - 未ログイン: 一時保存値（sessionStorage）
// 取得失敗・未解決・未設定のときは careerStage=null（区分なし）を返し、呼び出し側は全件表示にフォールバックする。

// 呼び出し側が同じ /users/me の取得結果を使い回せるよう公開する最小限のプロフィール形。
// （練習一覧のプロフィール誘導モーダルが completeness を使い、/users/me の重複取得を避ける）
export interface CurrentProfile {
  career_stage?: string | null;
  completeness?: { filled: number; total: number } | null;
}

export interface UseCareerStageResult {
  careerStage: CareerStageValue | null;
  // ログイン時に取得した /users/me のプロフィール。ゲスト時・未取得時・取得失敗時は null。
  profile: CurrentProfile | null;
}

// sessionStorage は購読不要（同一セッション中に外部から変わらず、ログイン成立時は
// プロフィール側へ切り替わる）ため、購読は no-op とする。
const subscribeGuestStore = () => () => {};

// このフックはヘッダー・画面下部のメニュー・画面本体から同じ描画で同時に呼ばれる
// （求人導線の出し分けがヘッダー側にも入ったため / #1749）。実行中の取得だけを
// 共有し、1回の描画で /users/me を重ねて叩かないようにする。
// 完了後は共有を捨てて次の画面遷移では取り直す。結果を持ち回すと、プロフィール
// 編集の直後に古い完成度が出てしまう。
//
// 取得できた結果と取得失敗を区別する。取得できたときは、プロフィールが無い場合も
// 「区分なし」として状態へ反映する必要がある（別アカウントへ切り替えたときに、
// 前のユーザーの区分が残ったまま求人導線の判定に使われるのを防ぐ）。
// 取得失敗のときだけ、直前の状態をそのまま残す。
type ProfileFetchResult =
  | { ok: true; profile: CurrentProfile | null }
  | { ok: false };

let inFlightProfile: {
  userId: string | undefined;
  revision: number;
  promise: Promise<ProfileFetchResult>;
} | null = null;

// 世代番号も鍵に含める。プロフィール更新で番号が変わると共有が外れて取り直しになり、
// 同じ番号のあいだは画面が何枚あっても取得は1回で済む。
function fetchCurrentProfile(
  userId: string | undefined,
  revision: number,
): Promise<ProfileFetchResult> {
  if (
    inFlightProfile &&
    inFlightProfile.userId === userId &&
    inFlightProfile.revision === revision
  ) {
    return inFlightProfile.promise;
  }

  const promise: Promise<ProfileFetchResult> = apiClient
    .get("/users/me")
    .then((res) => ({
      ok: true as const,
      profile: (res.data?.profile as CurrentProfile) ?? null,
    }))
    .catch(() => ({ ok: false as const }))
    .finally(() => {
      if (
        inFlightProfile?.userId === userId &&
        inFlightProfile?.revision === revision
      ) {
        inFlightProfile = null;
      }
    });

  inFlightProfile = { userId, revision, promise };
  return promise;
}

export function useCareerStage(): UseCareerStageResult {
  const { user } = useAuth();

  // ゲスト区分は sessionStorage という外部ストアから読む。useSyncExternalStore により
  // SSR/ハイドレーション時は null、マウント後にクライアント値へ切り替わる（ハイドレーション不整合を回避）。
  const guestStage = useSyncExternalStore(
    subscribeGuestStore,
    () => getGuestCareerStage(),
    () => null
  );

  // ログイン済はプロフィールを非同期取得する。取得失敗・未設定時は null（全件表示 / モーダル非表示）。
  const [profile, setProfile] = useState<CurrentProfile | null>(null);

  // プロフィールを書き換えたら世代番号が増え、下の取得が走り直す。
  // 新規登録直後に練習タイプを選んだとき、再読み込みを待たずに反映させるため。
  const revision = useSyncExternalStore(
    subscribeProfileRevalidation,
    getProfileRevision,
    getServerProfileRevision,
  );

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetchCurrentProfile(user.id, revision).then((result) => {
      // 取得失敗時は区分なし（全件表示 / モーダル非表示）にフォールバックする
      if (!cancelled && result.ok) setProfile(result.profile);
    });
    return () => {
      cancelled = true;
    };
  }, [user, revision]);

  const loginStage =
    profile?.career_stage === "new_grad" ||
    profile?.career_stage === "mid_career" ||
    profile?.career_stage === "high_school"
      ? profile.career_stage
      : null;

  return {
    careerStage: user ? loginStage : guestStage,
    profile: user ? profile : null,
  };
}
